'use client';

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ZodError } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from 'v5/design-system/Button';
import { cn } from 'v5/platform/dom/cn';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'v5/design-system/Tooltip';
import { EntityListRowErrorBoundary } from './EntityListRowErrorBoundary';
import { entityListRowIssueChromeClassName } from './EntityListRowRenderErrorPanel';
import { EntityListRowValidationIndicator } from './EntityListRowValidationIndicator';

export type ValidatedRowInvalidRenderPanelProps<TEntity> = {
  error: ZodError;
  /** Original JSON row when Zod failed; used for optional “try to render anyway”. */
  rawItem: unknown | undefined;
  renderItem: (entity: TEntity) => ReactNode;
  /**
   * Table invalid row: compact chrome; unsafe preview body is rendered on a second table row.
   */
  separatePreviewSurface?: boolean;
  /**
   * When “display anyway” is on, set on the preview root as `data-row-id` (e.g. E2E).
   * Used by grid invalid tiles; table uses a separate row for `data-row-id`.
   */
  displayAnywayDataRowId?: string;
  /** Controlled “display anyway” / unsafe preview (parent owns state). */
  unsafePreview: boolean;
  onUnsafePreviewChange: (enabled: boolean) => void;
  /**
   * Grid invalid tile: compact chrome + icon “display anyway”; preview body stays in-panel.
   */
  invalidGridTileChrome?: boolean;
};

/** Best-effort cast of raw API row for optional “display anyway” rendering. */
export function asEntityForUnsafeRender<TEntity>(raw: unknown): TEntity {
  return raw as TEntity;
}

/**
 * Shared UI for a row that failed Zod validation: summary, details popover, and
 * optional best-effort render of the raw payload inside a row error boundary.
 *
 * Supports two call shapes only: table invalid rows (`separatePreviewSurface`, see
 * {@link SortableEntityTable}) and grid invalid tiles (`invalidGridTileChrome`, see
 * {@link EntityGridListItem}).
 */
export function ValidatedRowInvalidRenderPanel<TEntity>(
  props: ValidatedRowInvalidRenderPanelProps<TEntity>
) {
  const { t } = useTranslation('patterns', { keyPrefix: 'EntityListRow' });
  const { unsafePreview, onUnsafePreviewChange } = props;
  const previewBodySeparate = props.separatePreviewSurface === true;
  const isGridTile = props.invalidGridTileChrome === true;
  const canTryUnsafe = props.rawItem !== undefined;

  const validationIndicator = (
    <EntityListRowValidationIndicator
      error={props.error}
      rawItem={props.rawItem}
      variant="link"
    />
  );

  const compactUnsafePreviewToolbar = (
    <div className="flex shrink-0 items-center justify-between gap-1.5 px-1.5 pb-1 pt-1.5">
      <div className="min-w-0 flex-1 overflow-hidden">
        {validationIndicator}
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondaryMuted"
            layout="icon"
            size="xs"
            className="size-7 shrink-0 bg-bg-default/90 shadow-sm backdrop-blur-xs"
            aria-label={t('hideUnsafePreview')}
            onClickCapture={e => {
              e.stopPropagation();
              onUnsafePreviewChange(false);
            }}
          >
            <EyeOff className="size-4" strokeWidth={2} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t('hideUnsafePreview')}</TooltipContent>
      </Tooltip>
    </div>
  );

  if (unsafePreview && canTryUnsafe && (previewBodySeparate || isGridTile)) {
    return (
      <div
        className={cn(
          'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-transparent'
        )}
        {...(props.displayAnywayDataRowId !== undefined
          ? { 'data-row-id': props.displayAnywayDataRowId }
          : {})}
      >
        {compactUnsafePreviewToolbar}
        {!previewBodySeparate && (
          <div className="min-h-0 min-w-0 flex-1 overflow-auto px-1.5 pb-1.5 pt-1">
            <EntityListRowErrorBoundary
              labels={{
                title: t('renderCrashed'),
                tryAgain: t('renderTryAgain')
              }}
            >
              {props.renderItem(
                asEntityForUnsafeRender<TEntity>(props.rawItem)
              )}
            </EntityListRowErrorBoundary>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        entityListRowIssueChromeClassName,
        'flex min-h-0 min-w-0 flex-1 flex-col rounded-md px-1.5 pb-1.5 pt-2'
      )}
    >
      <div className="flex min-w-0 flex-row flex-nowrap items-center justify-between gap-1.5 px-1">
        <div className="min-w-0 flex-1 overflow-hidden">
          {validationIndicator}
        </div>
        {canTryUnsafe && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="secondaryMuted"
                layout="icon"
                size="xs"
                className="shrink-0"
                aria-label={t('tryDisplayAnyway')}
                onClickCapture={e => {
                  e.stopPropagation();
                  onUnsafePreviewChange(true);
                }}
              >
                <Eye className="size-4" strokeWidth={2} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t('tryDisplayAnyway')}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
